import { ChevronDown, History, X } from "lucide-react";
import { useWorkflowsEditorWorkflows } from "../../context/workflows-editor-workflows-context";
import { useTranslation } from "react-i18next";
import useCurrentShopSuspense from "../../hooks/use-current-shop-suspense";
import { useWorkflowsEditorConfig } from "../../context/workflows-editor-config-context";
import { WorkflowV2ExecutionDto } from "@babackapp/private-api-client/types";
import { Badge } from "@babackapp/ui/components/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@babackapp/ui/components/collapsible";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { cn } from "@babackapp/ui/lib/utils";
import { hasActiveSuperpower } from "@babackapp/auth-client/utils/super-admin";
import { useLoggedViewer } from "@babackapp/private-api-client/client/use-logged-user";


function ExecutionItem({ execution }: { execution: WorkflowV2ExecutionDto }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation("trackoffice");
  const { data: user } = useLoggedViewer();
  // Raw errors for active super admins only (flag and MFA, BAB-3035), not any
  // @baback.co address: test accounts and former staff have one.
  const isBabackTeamUser = hasActiveSuperpower(user);

  const statusVariant =
    execution.status === "success" ? "success" :
      execution.status === "failed" ? "destructive" :
        "warning";

  const relativeTime = formatDistanceToNow(new Date(execution.executedAt), { addSuffix: true });
  const durationText = execution.duration ? `${(execution.duration / 1000).toFixed(2)}s` : "-";
  // Each engine records a domain-specific eventData shape.
  const { executionEventFields } = useWorkflowsEditorConfig();

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return "-";
    }
    if (typeof value === "object") {
      return "[Object]";
    }
    return String(value);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border-b border-border last:border-0">
        <CollapsibleTrigger className="w-full hover:bg-accent/50 transition-colors">
          <div className="flex items-center gap-2 p-3 text-sm">
            <ChevronDown className={cn("size-4 transition-transform shrink-0", isOpen && "rotate-180")} />
            <Badge variant={statusVariant} size="xs" className="shrink-0">
              {execution.status}
            </Badge>
            <span className="text-muted-foreground text-xs flex-1 text-left truncate">
              {relativeTime}
            </span>
            <span className="text-muted-foreground text-xs shrink-0">
              {durationText}
            </span>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 pt-1 space-y-3">
            {/* Event Data */}
            {executionEventFields.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {executionEventFields.map(({ key, labelKey, defaultLabel }) => (
                  <div key={key} className="text-xs flex flex-col">
                    <span className="text-muted-foreground">{t(labelKey, defaultLabel)}</span>
                    <span className="text-foreground font-medium">
                      {formatValue(
                        (execution.eventData as Record<string, unknown>)[key],
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Error */}
            {!!execution.error && isBabackTeamUser &&
							<div>
								<div className="text-xs font-medium mb-1.5 text-foreground">
                  {t("workflowsEditor.error", "Error")}
								</div>
								<div className="bg-destructive/10 text-destructive rounded p-2 text-xs break-words">
                  {execution.error}
								</div>
							</div>
            }
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}


export function WorkflowExecutionsList({ onClose }: { onClose?: () => void }) {
  const { currentWorkflow } = useWorkflowsEditorWorkflows();
  const { data: shop } = useCurrentShopSuspense();
  const { api } = useWorkflowsEditorConfig();
  const { t } = useTranslation("trackoffice");
  const { data: executions } = api.useWorkflowExecutions(shop.id, currentWorkflow?.id || "");
  const { data, totalCount } = executions || { data: [], totalCount: 0 };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between border-b px-3 py-2">
        <span className="flex items-center gap-1 text-sm font-medium">
          <History className="size-3.5" />
          {t("workflowsEditor.workflowExecutions", "Executions")}
          <span className="ml-1 text-xs text-muted-foreground">{totalCount ?? 0}</span>
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label={t("workflowsEditor.listing.close", "Close")}
          >
            <X className="size-4" />
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
        {data && data.length > 0 ? (
          data.map((execution) => (
            <ExecutionItem key={execution.id} execution={execution} />
          ))
        ) : (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {t("workflowsEditor.noExecutions", "No executions yet")}
          </div>
        )}
      </div>
    </div>
  );
}
